import { createApi, fetchBaseQuery, BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { getApiConfig } from '@/config/api';
import { getRequestLanguage } from '@/utils/requestLanguage';
import type { ApiResponse } from '@/types';

// Bank Account entity type
export interface BankAccountEntity {
  id: string;
  outletId: string;
  bankName: string;
  accountNumber: string;
  accountHolder: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    transactions: number;
  };
}

// DTOs
export interface CreateBankAccountDto {
  bankName: string;
  accountNumber: string;
  accountHolder: string;
  isActive?: boolean;
}

export interface UpdateBankAccountDto {
  bankName?: string;
  accountNumber?: string;
  accountHolder?: string;
  isActive?: boolean;
}

// Create a dynamic base query that always uses the current hostname
const dynamicBaseQuery: BaseQueryFn = async (args, api, extraOptions) => {
  const config = getApiConfig();

  const baseQuery = fetchBaseQuery({
    baseUrl: config.API_URL,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('token');
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      headers.set('accept', 'application/json');
      headers.set('content-type', 'application/json');
      headers.set('accept-language', getRequestLanguage());
      return headers;
    },
  });

  return baseQuery(args, api, extraOptions);
};

export const bankAccountApi = createApi({
  reducerPath: 'bankAccountApi',
  baseQuery: dynamicBaseQuery,
  tagTypes: ['BankAccounts'],
  endpoints: (builder) => ({
    // Get all bank accounts for outlet
    getBankAccounts: builder.query<ApiResponse<BankAccountEntity[]>, { outletId?: string; activeOnly?: boolean }>({
      query: ({ outletId, activeOnly }) => {
        const params = new URLSearchParams();
        if (outletId) params.append('outletId', outletId);
        if (activeOnly) params.append('activeOnly', 'true');
        return {
          url: `/settings/bank-accounts?${params.toString()}`,
          method: 'GET',
        };
      },
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ id }) => ({ type: 'BankAccounts' as const, id })),
            { type: 'BankAccounts', id: 'LIST' },
          ]
          : [{ type: 'BankAccounts', id: 'LIST' }],
    }),

    // Get single bank account
    getBankAccount: builder.query<ApiResponse<BankAccountEntity>, { id: string; outletId?: string }>({
      query: ({ id, outletId }) => {
        const params = new URLSearchParams();
        if (outletId) params.append('outletId', outletId);
        return {
          url: `/settings/bank-accounts/${id}?${params.toString()}`,
          method: 'GET',
        };
      },
      providesTags: (_, __, { id }) => [{ type: 'BankAccounts', id }],
    }),

    // Create bank account
    createBankAccount: builder.mutation<
      ApiResponse<BankAccountEntity>,
      { data: CreateBankAccountDto; outletId?: string }
    >({
      query: ({ data, outletId }) => {
        const params = new URLSearchParams();
        if (outletId) params.append('outletId', outletId);
        return {
          url: `/settings/bank-accounts?${params.toString()}`,
          method: 'POST',
          body: data,
        };
      },
      invalidatesTags: [{ type: 'BankAccounts', id: 'LIST' }],
    }),

    // Update bank account
    updateBankAccount: builder.mutation<
      ApiResponse<BankAccountEntity>,
      { id: string; data: UpdateBankAccountDto; outletId?: string }
    >({
      query: ({ id, data, outletId }) => {
        const params = new URLSearchParams();
        if (outletId) params.append('outletId', outletId);
        return {
          url: `/settings/bank-accounts/${id}?${params.toString()}`,
          method: 'PUT',
          body: data,
        };
      },
      invalidatesTags: (_, __, { id }) => [
        { type: 'BankAccounts', id },
        { type: 'BankAccounts', id: 'LIST' },
      ],
    }),

    // Toggle bank account status
    toggleBankAccountStatus: builder.mutation<
      ApiResponse<BankAccountEntity>,
      { id: string; isActive: boolean; outletId?: string }
    >({
      query: ({ id, isActive, outletId }) => {
        const params = new URLSearchParams();
        if (outletId) params.append('outletId', outletId);
        return {
          url: `/settings/bank-accounts/${id}/status?${params.toString()}`,
          method: 'PATCH',
          body: { isActive },
        };
      },
      invalidatesTags: (_, __, { id }) => [
        { type: 'BankAccounts', id },
        { type: 'BankAccounts', id: 'LIST' },
      ],
    }),

    // Delete bank account
    deleteBankAccount: builder.mutation<ApiResponse<{ message: string }>, { id: string; outletId?: string }>({
      query: ({ id, outletId }) => {
        const params = new URLSearchParams();
        if (outletId) params.append('outletId', outletId);
        return {
          url: `/settings/bank-accounts/${id}?${params.toString()}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: [{ type: 'BankAccounts', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetBankAccountsQuery,
  useGetBankAccountQuery,
  useCreateBankAccountMutation,
  useUpdateBankAccountMutation,
  useToggleBankAccountStatusMutation,
  useDeleteBankAccountMutation,
} = bankAccountApi;
